import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Edit, Upload, Utensils, Users, History, Download, Printer, Share2, IndianRupee, Loader2, Calculator, Beef } from 'lucide-react';
import FMBTakhmeenPage from '../components/FMBTakhmeenPage';
import ZabhiyatTab from '../components/ZabhiyatTab';

// Custom hook for debounced value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function FMBPage() {
  const { api, isAdmin, canExportExcel, canExportPDF, canEditData, isSuperAdmin, isFMBViewer, isZabhiyat } = useAuth();
  const navigate = useNavigate();
  // Default tab based on role: Zabhiyat role goes directly to zabhiyat tab
  const [activeMainTab, setActiveMainTab] = useState(isZabhiyat ? 'zabhiyat' : 'thaali');
  const [fmbList, setFmbList] = useState([]);
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 500); // Increased debounce for better performance
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showPaymentUploadDialog, setShowPaymentUploadDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAll, setShowAll] = useState(true); // Default to show all
  const pageSize = showAll ? 500 : 50; // Show 500 when "show all" is enabled

  const fetchFMBList = useCallback(async () => {
    // Only fetch when Thaali tab is active
    if (activeMainTab !== 'thaali') return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());
      if (search) params.append('search', search);
      if (selectedMohalla) params.append('mohalla', selectedMohalla);
      if (selectedSector) params.append('sector', selectedSector);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedPaymentStatus) params.append('payment_status', selectedPaymentStatus);
      
      const response = await api.get(`/fmb?${params}`);
      setFmbList(response.data.fmb_list || []);
      setTotalCount(response.data.total || response.data.fmb_list?.length || 0);
      setTotalPages(response.data.pages || Math.ceil((response.data.total || response.data.fmb_list?.length || 0) / pageSize));
    } catch (error) {
      toast.error('Failed to fetch FMB list');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [api, search, selectedMohalla, selectedSector, selectedStatus, selectedPaymentStatus, page, activeMainTab, pageSize]);

  const fetchMohallas = useCallback(async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  }, [api]);

  const fetchSectors = useCallback(async () => {
    try {
      const params = selectedMohalla ? `?mohalla=${selectedMohalla}` : '';
      const response = await api.get(`/sectors${params}`);
      setSectors(response.data.sectors);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  }, [api, selectedMohalla]);

  // Fetch FMB list only when thaali tab is active and filters change
  useEffect(() => {
    if (activeMainTab === 'thaali') {
      fetchFMBList();
    }
  }, [activeMainTab, search, selectedMohalla, selectedSector, selectedStatus, selectedPaymentStatus, page]);

  // Fetch mohallas once on mount
  useEffect(() => {
    fetchMohallas();
  }, []);

  // Fetch sectors when mohalla changes
  useEffect(() => {
    fetchSectors();
  }, [selectedMohalla]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, selectedMohalla, selectedSector, selectedStatus, selectedPaymentStatus]);

  const handleUpdateFMB = async (hofId, thaaliNumber, fmbStatus) => {
    try {
      await api.put(`/fmb/${hofId}?thaali_number=${encodeURIComponent(thaaliNumber)}&fmb_status=${encodeURIComponent(fmbStatus)}`);
      toast.success('FMB updated successfully');
      setEditItem(null);
      fetchFMBList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  // Update payment status
  const [updatingPayment, setUpdatingPayment] = useState(null);
  
  const handleUpdatePaymentStatus = async (hofId, newStatus) => {
    setUpdatingPayment(hofId);
    try {
      await api.put(`/fmb/${hofId}/payment?payment_status=${encodeURIComponent(newStatus)}`);
      toast.success(`Payment marked as ${newStatus}`);
      fetchFMBList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment status');
    } finally {
      setUpdatingPayment(null);
    }
  };

  const goingCount = useMemo(() => fmbList.filter(f => f.fmb_status === 'Going').length, [fmbList]);
  const notGoingCount = useMemo(() => fmbList.filter(f => f.fmb_status === 'Not Going').length, [fmbList]);
  const paidCount = useMemo(() => fmbList.filter(f => f.fmb_status === 'Going' && f.fmb_payment_status === 'Paid').length, [fmbList]);
  const unpaidCount = useMemo(() => fmbList.filter(f => f.fmb_status === 'Going' && f.fmb_payment_status !== 'Paid').length, [fmbList]);

  const handleExport = async (format = 'excel') => {
    try {
      setExporting(true);
      let url = `/export/universal?export_type=fmb&format=${format}`;
      if (selectedMohalla) url += `&mohalla=${encodeURIComponent(selectedMohalla)}`;
      if (selectedSector) url += `&sector=${encodeURIComponent(selectedSector)}`;
      if (selectedStatus) url += `&fmb_status=${encodeURIComponent(selectedStatus)}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create blob
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const fileName = `fmb_export_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Mobile-friendly download
      if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
        // For mobile: open in new tab (works better on iOS/Android)
        const blobUrl = window.URL.createObjectURL(blob);
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          // Fallback if popup blocked
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      } else {
        // Desktop download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
      
      toast.success(`${format.toUpperCase()} file downloaded successfully`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    try {
      let url = `/export/universal?export_type=fmb&format=excel`;
      if (selectedMohalla) url += `&mohalla=${encodeURIComponent(selectedMohalla)}`;
      if (selectedSector) url += `&sector=${encodeURIComponent(selectedSector)}`;
      if (selectedStatus) url += `&fmb_status=${encodeURIComponent(selectedStatus)}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      const file = new File([response.data], `fmb_export.xlsx`, { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'FMB Export',
          text: 'FMB Thaali data export'
        });
        toast.success('Shared successfully');
      } else {
        // Fallback: download the file instead
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `fmb_export_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.info('Share not available on this device. File downloaded instead.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to share: ' + (error.message || 'Unknown error'));
      }
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fmb-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">FMB Thaali Management</h1>
          <p className="text-sm text-slate-500">Manage Faiz-ul-Mawaid-il-Burhaniyah thaali distribution</p>
        </div>
      </div>

      {/* Main Tabs - Thaali vs Takhmeen vs Zabhiyat */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="print:hidden">
        <TabsList className={`grid w-full max-w-lg ${isFMBViewer ? 'grid-cols-2' : isZabhiyat ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {!isZabhiyat && (
            <TabsTrigger value="thaali" className="gap-2">
              <Utensils className="w-4 h-4" />
              Thaali List
            </TabsTrigger>
          )}
          {!isFMBViewer && !isZabhiyat && (
            <TabsTrigger value="takhmeen" className="gap-2">
              <Calculator className="w-4 h-4" />
              Takhmeen
            </TabsTrigger>
          )}
          <TabsTrigger value="zabhiyat" className="gap-2">
            <Beef className="w-4 h-4" />
            Zabhiyat
          </TabsTrigger>
        </TabsList>

        {/* Thaali Tab */}
        <TabsContent value="thaali" className="space-y-6">
          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <Button onClick={() => setShowPaymentUploadDialog(true)} variant="outline" size="sm" className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100" data-testid="upload-payment-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Payment
              </Button>
            )}
            {(canExportPDF || canExportExcel) && (
              <>
                <Button onClick={() => setShowExportDialog(true)} variant="outline" size="sm" data-testid="fmb-export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button onClick={handlePrint} variant="outline" size="sm" data-testid="fmb-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </>
            )}
            {isAdmin && <FMBUploadDialog api={api} onSuccess={fetchFMBList} />}
          </div>

      {/* Payment Upload Dialog */}
      <PaymentUploadDialog 
        open={showPaymentUploadDialog} 
        onOpenChange={setShowPaymentUploadDialog}
        api={api}
        onSuccess={fetchFMBList}
      />

      {/* Export Format Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export FMB Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">Choose export format:</p>
            <div className={`grid gap-4 ${canExportExcel ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {/* Excel option - Super Admin ONLY */}
              {canExportExcel && (
                <Button 
                  variant={exportFormat === 'excel' ? 'default' : 'outline'}
                  className={exportFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setExportFormat('excel')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Excel (.xlsx)
                </Button>
              )}
              <Button 
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                className={exportFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setExportFormat('pdf')}
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
            {!canExportExcel && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                📋 Excel export is restricted to Super Admin only
              </p>
            )}
            {selectedMohalla && (
              <p className="text-xs text-slate-500">Filtered by Mohalla: {selectedMohalla}</p>
            )}
            {selectedSector && (
              <p className="text-xs text-slate-500">Filtered by Sector: {selectedSector}</p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={() => handleExport(exportFormat)} 
              disabled={exporting}
              className="bg-navy hover:bg-navy/90"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold text-center">FMB Thaali Management</h1>
        <p className="text-center text-sm text-gray-600">Total: {fmbList.length} families | Going: {goingCount} | Not Going: {notGoingCount} | Paid: {paidCount} | Unpaid: {unpaidCount}</p>
      </div>

      {/* Stats - FMB Viewer only sees Going/Not Going/Total + Paid/Unpaid */}
      <div className={`grid gap-3 sm:gap-4 print:hidden ${isFMBViewer ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'}`}>
        <Card 
          className="border-0 shadow-card bg-emerald-50 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/drilldown/fmb/Going')}
          data-testid="fmb-going-card"
        >
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-emerald-600">Going</p>
              <p className="text-xl sm:text-2xl font-heading font-bold text-emerald-700">{goingCount}</p>
              <p className="text-[10px] sm:text-xs text-emerald-500 mt-1">View list →</p>
            </div>
            <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-card bg-amber-50 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/drilldown/fmb/Not Going')}
          data-testid="fmb-not-going-card"
        >
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-amber-600">Not Going</p>
              <p className="text-xl sm:text-2xl font-heading font-bold text-amber-700">{notGoingCount}</p>
              <p className="text-[10px] sm:text-xs text-amber-500 mt-1">View list →</p>
            </div>
            <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-card bg-green-50 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => { setSelectedPaymentStatus('Paid'); setSelectedStatus(''); }}
          data-testid="fmb-paid-card"
        >
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-green-600">Paid</p>
              <p className="text-xl sm:text-2xl font-heading font-bold text-green-700">{paidCount}</p>
              <p className="text-[10px] sm:text-xs text-green-500 mt-1">Click to filter →</p>
            </div>
            <IndianRupee className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-card bg-red-50 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => { setSelectedPaymentStatus('Unpaid'); setSelectedStatus(''); }}
          data-testid="fmb-unpaid-card"
        >
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-red-600">Unpaid</p>
              <p className="text-xl sm:text-2xl font-heading font-bold text-red-700">{unpaidCount}</p>
              <p className="text-[10px] sm:text-xs text-red-500 mt-1">Click to filter →</p>
            </div>
            <IndianRupee className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card bg-blue-50 col-span-2 sm:col-span-1">
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-blue-600">Total Families</p>
              <p className="text-xl sm:text-2xl font-heading font-bold text-blue-700">{fmbList.length}</p>
            </div>
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
          </CardContent>
        </Card>
      </div>

      {/* Active Filter Indicator */}
      {selectedPaymentStatus && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg print:hidden">
          <span className="text-sm text-blue-700">Filtering by: <strong>{selectedPaymentStatus}</strong></span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSelectedPaymentStatus('')}
            className="h-6 px-2 text-xs"
          >
            Clear Filter ✕
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by ITS, name, mobile, thaali..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                data-testid="fmb-search-input"
              />
            </div>
            <Select value={selectedMohalla} onValueChange={(v) => setSelectedMohalla(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="fmb-mohalla-filter">
                <SelectValue placeholder="Filter by Mohalla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedSector} onValueChange={(v) => setSelectedSector(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="fmb-sector-filter">
                <SelectValue placeholder="Filter by Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(s => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={(v) => { setSelectedStatus(v === 'all' ? '' : v); if (v) setSelectedPaymentStatus(''); }}>
              <SelectTrigger data-testid="fmb-status-filter">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Going">Going</SelectItem>
                <SelectItem value="Not Going">Not Going</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPaymentStatus} onValueChange={(v) => { setSelectedPaymentStatus(v === 'all' ? '' : v); if (v) setSelectedStatus(''); }}>
              <SelectTrigger data-testid="fmb-payment-filter">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Unpaid">Unpaid (Balance)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden print:shadow-none print:border">
        <div className="overflow-x-auto">
          <Table className="data-table min-w-[600px] md:min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Sr</TableHead>
                <TableHead className="whitespace-nowrap">HOF ITS</TableHead>
                <TableHead className="whitespace-nowrap min-w-[150px]">HOF Name</TableHead>
                <TableHead className="hidden md:table-cell whitespace-nowrap">Mohalla</TableHead>
                <TableHead className="whitespace-nowrap">Sector</TableHead>
                <TableHead className="whitespace-nowrap">Members</TableHead>
                <TableHead className="whitespace-nowrap">Thaali No</TableHead>
                {/* Size column - Admin & FMB Viewer - NOT in print/export */}
                {(isAdmin || isFMBViewer) && <TableHead className="whitespace-nowrap print:hidden">Size</TableHead>}
                {/* Zabhiyat column - FMB Viewer only */}
                {isFMBViewer && <TableHead className="whitespace-nowrap">Zabhiyat</TableHead>}
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="whitespace-nowrap text-center">Payment</TableHead>
                {canEditData && <TableHead className="text-right print:hidden whitespace-nowrap">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loading && initialLoad) ? (
                <TableRow>
                  <TableCell colSpan={(isAdmin || isFMBViewer) ? 11 : 10} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : fmbList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(isAdmin || isFMBViewer) ? 11 : 10} className="text-center py-8 text-slate-500">
                    {loading ? 'Loading...' : 'No FMB records found'}
                  </TableCell>
                </TableRow>
              ) : (
                fmbList.map((item, idx) => (
                  <TableRow key={item.id} data-testid={`fmb-row-${item.id}`}>
                    <TableCell className="text-sm">{idx + 1}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{item.hof_its || '-'}</TableCell>
                    <TableCell className="font-medium text-sm">{item.hof_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{item.mohalla || '-'}</TableCell>
                    <TableCell className="text-sm">{item.sector || '-'}</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 cursor-help">
                              <Badge className="bg-slate-100 text-slate-700 text-xs">
                                {item.member_count || 0}
                              </Badge>
                              <span className="text-[10px] text-slate-400">
                                (<span className="text-green-600">{item.misaq_count || 0}</span>/<span className="text-amber-600">{item.non_misaq_count || 0}</span>)
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs space-y-1">
                              <p>Total Members: <span className="font-semibold">{item.member_count || 0}</span></p>
                              <p>Misaq Done: <span className="font-semibold text-green-600">{item.misaq_count || 0}</span></p>
                              <p>Non-Misaq: <span className="font-semibold text-amber-600">{item.non_misaq_count || 0}</span></p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-semibold text-navy text-sm">
                          {item.thaali_number || '-'}
                        </span>
                        {item.old_thaali_number && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                                  <History className="w-3 h-3 text-amber-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Old Thaali: {item.old_thaali_number}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    {/* Size cell - Admin & FMB Viewer - NOT in print/export */}
                    {(isAdmin || isFMBViewer) && (
                      <TableCell className="print:hidden">
                        <Badge className={`text-xs ${
                          item.thaali_size === 'D' ? 'bg-purple-100 text-purple-700' :
                          item.thaali_size === 'C' ? 'bg-blue-100 text-blue-700' :
                          item.thaali_size === 'B' ? 'bg-green-100 text-green-700' :
                          item.thaali_size === 'A' ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {item.thaali_size || '-'}
                        </Badge>
                      </TableCell>
                    )}
                    {/* Zabhiyat column - FMB Viewer only */}
                    {isFMBViewer && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-navy">{item.zabhiyat || 0}</span>
                          {item.zabhiyat_done > 0 && (
                            <span className="text-xs text-green-600">({item.zabhiyat_done} done)</span>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge className={`text-xs ${item.fmb_status === 'Going' ? 'badge-success' : 'badge-warning'}`}>
                        {item.fmb_status}
                      </Badge>
                    </TableCell>
                    {/* Payment Status - allow marking for FMB Viewer too */}
                    <TableCell className="text-center">
                      {item.fmb_status === 'Going' ? (
                        <div className="flex items-center justify-center gap-2">
                          {updatingPayment === item.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-navy" />
                          ) : (
                            <Checkbox
                              checked={item.fmb_payment_status === 'Paid'}
                              onCheckedChange={(checked) => handleUpdatePaymentStatus(item.id, checked ? 'Paid' : 'Unpaid')}
                              data-testid={`payment-checkbox-${item.id}`}
                            />
                          )}
                          <Badge className={`text-xs ${item.fmb_payment_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.fmb_payment_status || 'Unpaid'}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">N/A</span>
                      )}
                    </TableCell>
                    {canEditData && (
                      <TableCell className="text-right print:hidden">
                        <Button variant="ghost" size="sm" onClick={() => setEditItem(item)} data-testid={`edit-fmb-${item.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {/* Pagination / Show All Toggle */}
      <div className="flex justify-center items-center gap-2 py-4 print:hidden">
        {!showAll && totalPages > 1 && (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </>
        )}
        <Button 
          variant={showAll ? "default" : "outline"} 
          size="sm"
          onClick={() => { setShowAll(!showAll); setPage(1); }}
          className={showAll ? "bg-blue-600" : ""}
        >
          {showAll ? `Showing All (${totalCount})` : `Show All`}
        </Button>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>

        </TabsContent>

        {/* Takhmeen Tab */}
        <TabsContent value="takhmeen">
          <FMBTakhmeenPage />
        </TabsContent>

        {/* Zabhiyat Tab */}
        <TabsContent value="zabhiyat">
          <ZabhiyatTab />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {editItem && (
        <FMBEditDialog 
          item={editItem} 
          onSubmit={handleUpdateFMB} 
          onClose={() => setEditItem(null)} 
        />
      )}
    </div>
  );
}

function FMBEditDialog({ item, onSubmit, onClose }) {
  const [thaaliNumber, setThaaliNumber] = useState(item.thaali_number || '');
  const [fmbStatus, setFmbStatus] = useState(item.fmb_status || 'Not Going');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit(item.id, thaaliNumber, fmbStatus);
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit FMB - {item.hof_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="form-label">Primary Thaali Number</Label>
            <Input 
              value={thaaliNumber} 
              onChange={(e) => setThaaliNumber(e.target.value)}
              data-testid="edit-thaali-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Secondary thaali will auto-generate as: {thaaliNumber ? `${thaaliNumber}A` : '-'}
            </p>
          </div>
          
          {item.old_thaali_number && (
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700">
                <History className="w-4 h-4" />
                <span className="text-sm font-medium">Previous Thaali History</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">{item.old_thaali_number}</p>
            </div>
          )}
          
          <div>
            <Label className="form-label">FMB Status</Label>
            <Select value={fmbStatus} onValueChange={setFmbStatus}>
              <SelectTrigger data-testid="edit-fmb-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Going">Going</SelectItem>
                <SelectItem value="Not Going">Not Going</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSubmit} className="bg-navy hover:bg-navy-light" disabled={loading} data-testid="save-fmb-btn">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FMBUploadDialog({ api, onSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/fmb', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Updated ${response.data.updated} families`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      setIsOpen(false);
      setFile(null);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Button variant="outline" onClick={() => setIsOpen(true)} data-testid="upload-fmb-btn">
        <Upload className="w-4 h-4 mr-2" />
        Upload FMB
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload FMB Thaali Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="fmb-file-upload"
            />
            <label htmlFor="fmb-file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">{file ? file.name : 'Click to select Excel file'}</p>
              <p className="text-xs text-slate-400 mt-1">Required columns: HOF ID, Thaali Number</p>
            </label>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpload} disabled={!file || loading} className="bg-navy">
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PaymentUploadDialog({ open, onOpenChange, api, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/fmb/payment/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      toast.success(`Updated ${response.data.updated_count} families`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-green-600" />
            Upload Payment Status
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 font-medium">Excel Format:</p>
            <ul className="text-xs text-blue-600 mt-1 space-y-1">
              <li>• <strong>Name</strong> column: HOF full name</li>
              <li>• <strong>Status</strong> column: <span className="font-mono bg-blue-100 px-1 rounded">YES</span> = Paid, <span className="font-mono bg-blue-100 px-1 rounded">BALANCE</span> = Unpaid</li>
              <li>• <strong>ITS</strong> column (optional): For better matching</li>
            </ul>
          </div>
          
          <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-green-400 transition-colors">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
              className="hidden"
              id="payment-file-upload"
            />
            <label htmlFor="payment-file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-green-500 mb-2" />
              <p className="text-sm text-slate-600">{file ? file.name : 'Click to select Excel file'}</p>
              <p className="text-xs text-slate-400 mt-1">Supported: .xlsx, .xls</p>
            </label>
          </div>
          
          {result && (
            <div className="space-y-2">
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  ✓ Updated: <strong>{result.updated_count}</strong> families
                </p>
              </div>
              {result.unpaid_list_synced && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    ✓ <strong>FMB Unpaid List</strong> auto-synced in Custom Lists
                  </p>
                </div>
              )}
              {result.not_found_count > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700 font-medium">
                    ⚠ Not Found: {result.not_found_count} families
                  </p>
                  {result.not_found?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-xs text-amber-600">
                        {result.not_found.slice(0, 10).join(', ')}
                        {result.not_found.length > 10 && ` ... and ${result.not_found.length - 10} more`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button onClick={handleUpload} disabled={!file || loading} className="bg-green-600 hover:bg-green-700">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Update
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
